import React, { useState, useEffect, useContext } from "react";
import "./ProductCatalogue.css";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/Services/Auth";
import Input from "../../Components/Input/Input";
import Message from "../../Components/Message/Message";

import { FaSearch, FaTh, FaList, FaImages } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { BiCategory } from "react-icons/bi";

import { get_all_stock } from "../../context/Services/Stock";
import { get_all_categories, get_product_images } from "../../context/Services/Settings";

const ProductCatalogue = () => {
    const { t } = useTranslation();
    const { userData } = useContext(AuthContext);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [viewMode, setViewMode] = useState("grid"); // grid or list

    // Product detail modal
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productImages, setProductImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loadingImages, setLoadingImages] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [stockRes, categoriesRes] = await Promise.all([
                get_all_stock({ query: searchQuery, category: selectedCategory }),
                get_all_categories()
            ]);

            setProducts(stockRes?.data?.stock || stockRes?.stock || []);
            setCategories(categoriesRes?.data || []);
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProductImages = async (productId) => {
        setLoadingImages(true);
        try {
            const res = await get_product_images(productId);
            setProductImages(res?.data || []);
        } catch (error) {
            console.error("Failed to fetch images:", error);
            setProductImages([]);
        } finally {
            setLoadingImages(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedCategory]);

    const handleSearch = () => {
        fetchData();
    };

    const handleProductClick = async (product) => {
        setSelectedProduct(product);
        setCurrentImageIndex(0);
        await fetchProductImages(product._id);
    };

    const closeModal = () => {
        setSelectedProduct(null);
        setProductImages([]);
        setCurrentImageIndex(0);
    };

    const filteredProducts = products.filter(
        (p) => p.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <Message loading={loading} status={{ status: loading }} />

            <div className="catalogue_container">
                {/* Header */}
                <div className="catalogue_header">
                    <div className="catalogue_title_section">
                        <h2>{t("Catalogue Produits")}</h2>
                        <span className="product_count">{filteredProducts.length} {t("produits")}</span>
                    </div>

                    <div className="catalogue_controls">
                        {/* View Mode Toggle */}
                        <div className="view_toggle">
                            <button
                                className={`view_btn ${viewMode === "grid" ? "active" : ""}`}
                                onClick={() => setViewMode("grid")}
                            >
                                <FaTh />
                            </button>
                            <button
                                className={`view_btn ${viewMode === "list" ? "active" : ""}`}
                                onClick={() => setViewMode("list")}
                            >
                                <FaList />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="catalogue_filters">
                    <div className="search_section">
                        <Input
                            icon={<FaSearch className="icon_m" />}
                            placeholder={t("Rechercher un produit...")}
                            setChange={(e) => setSearchQuery(e)}
                            value={searchQuery}
                            customInputStyle="search_input_edited"
                        />
                    </div>

                    <div className="category_filters">
                        <button
                            className={`category_chip ${selectedCategory === null ? "active" : ""}`}
                            onClick={() => setSelectedCategory(null)}
                        >
                            {t("Tous")}
                        </button>
                        {categories.map((cat) => (
                            <button
                                key={cat._id}
                                className={`category_chip ${selectedCategory === cat._id ? "active" : ""}`}
                                style={selectedCategory === cat._id ? {
                                    backgroundColor: cat.bg_color,
                                    color: cat.text_color,
                                    borderColor: cat.bg_color
                                } : {}}
                                onClick={() => setSelectedCategory(cat._id)}
                            >
                                {cat.value}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Products */}
                {filteredProducts.length === 0 ? (
                    <div className="empty_state">
                        <BiCategory size={64} color="#ddd" />
                        <p>{t("Aucun produit trouvé")}</p>
                    </div>
                ) : viewMode === "grid" ? (
                    <div className="products_grid">
                        {filteredProducts.map((product) => (
                            <div
                                key={product._id}
                                className="product_card"
                                onClick={() => handleProductClick(product)}
                            >
                                <div className="product_image_container">
                                    {product.principal_image || product.image_path ? (
                                        <img src={product.principal_image || product.image_path} alt={product.product_name} />
                                    ) : (
                                        <div className="placeholder_image" style={{ backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyCenter: 'center', width: '100%', height: '100%' }}>
                                            <FaImages size={40} color="#ccc" />
                                        </div>
                                    )}
                                    {product.category && (
                                        <span
                                            className="category_badge"
                                            style={{
                                                backgroundColor: product.category.bg_color,
                                                color: product.category.text_color
                                            }}
                                        >
                                            {product.category.value}
                                        </span>
                                    )}
                                </div>
                                <div className="product_details">
                                    <h4>{product.product_name}</h4>
                                    <div className="product_meta">
                                        <span className="stock_info">
                                            Stock: <strong>{product.quantity}</strong>
                                        </span>
                                        {userData?.role === "admin" && product.prix_de_vente > 0 && (
                                            <span className="price_info">
                                                {product.prix_de_vente} MAD
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="products_list">
                        {filteredProducts.map((product) => (
                            <div
                                key={product._id}
                                className="product_list_item"
                                onClick={() => handleProductClick(product)}
                            >
                                <div className="list_item_image">
                                    {product.principal_image || product.image_path ? (
                                        <img src={product.principal_image || product.image_path} alt={product.product_name} />
                                    ) : (
                                        <div className="placeholder_image_small" style={{ backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyCenter: 'center' }}>
                                            <FaImages size={24} color="#ccc" />
                                        </div>
                                    )}
                                </div>
                                <div className="list_item_info">
                                    <h4>{product.product_name}</h4>
                                    {product.type && <span className="type_text">{product.type}</span>}
                                </div>
                                {product.category && (
                                    <span
                                        className="category_badge_list"
                                        style={{
                                            backgroundColor: product.category.bg_color,
                                            color: product.category.text_color
                                        }}
                                    >
                                        {product.category.value}
                                    </span>
                                )}
                                <div className="list_item_stock">
                                    <span>Stock: {product.quantity}</span>
                                </div>
                                {userData?.role === "admin" && (
                                    <div className="list_item_price">
                                        {product.prix_de_vente > 0 ? `${product.prix_de_vente} MAD` : "-"}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Product Detail Modal */}
            {selectedProduct && (
                <div className="modal_overlay" onClick={closeModal}>
                    <div className="modal_content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal_close_btn" onClick={closeModal}>
                            <IoMdClose size={24} />
                        </button>

                        <div className="modal_body">
                            {/* Image Gallery */}
                            <div className="modal_gallery">
                                {loadingImages ? (
                                    <div className="gallery_loading">
                                        <div className="spinner"></div>
                                    </div>
                                ) : productImages.length > 0 ? (
                                    <>
                                        <div className="main_image">
                                            <img
                                                src={productImages[currentImageIndex]?.url || productImages[currentImageIndex]?.image_path}
                                                alt={selectedProduct.product_name}
                                            />
                                        </div>
                                        {productImages.length > 1 && (
                                            <div className="thumbnails">
                                                {productImages.map((img, index) => (
                                                    <div
                                                        key={img._id}
                                                        className={`thumbnail ${currentImageIndex === index ? "active" : ""}`}
                                                        onClick={() => setCurrentImageIndex(index)}
                                                    >
                                                        <img src={img.url || img.image_path} alt={`Thumbnail ${index + 1}`} />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (selectedProduct.principal_image || selectedProduct.image_path) ? (
                                    <div className="main_image">
                                        <img src={selectedProduct.principal_image || selectedProduct.image_path} alt={selectedProduct.product_name} />
                                    </div>
                                ) : (
                                    <div className="no_image" style={{ backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '40px' }}>
                                        <FaImages size={64} color="#ccc" />
                                        <p>{t("Aucune image")}</p>
                                    </div>
                                )}
                            </div>

                            {/* Product Info */}
                            <div className="modal_info">
                                <h2>{selectedProduct.product_name}</h2>

                                {selectedProduct.category && (
                                    <span
                                        className="modal_category_badge"
                                        style={{
                                            backgroundColor: selectedProduct.category.bg_color,
                                            color: selectedProduct.category.text_color
                                        }}
                                    >
                                        {selectedProduct.category.value}
                                    </span>
                                )}

                                <div className="info_rows">
                                    {selectedProduct.type && (
                                        <div className="info_row">
                                            <span className="info_label">{t("Type")}</span>
                                            <span className="info_value">{selectedProduct.type}</span>
                                        </div>
                                    )}

                                    <div className="info_row">
                                        <span className="info_label">{t("Stock disponible")}</span>
                                        <span className={`info_value ${selectedProduct.quantity > 0 ? "success" : "danger"}`}>
                                            {selectedProduct.quantity} {t("unités")}
                                        </span>
                                    </div>

                                    {userData?.role === "admin" && selectedProduct.prix_de_vente > 0 && (
                                        <div className="info_row">
                                            <span className="info_label">{t("Prix de vente min.")}</span>
                                            <span className="info_value">{selectedProduct.prix_de_vente} MAD</span>
                                        </div>
                                    )}

                                    {selectedProduct.product_note && (
                                        <div className="info_row notes">
                                            <span className="info_label">{t("Notes")}</span>
                                            <p className="info_note">{selectedProduct.product_note}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProductCatalogue;
